import React, { useState, useEffect } from 'react';
import { Modal, DatePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import Dropdown from 'components/Elements/Dropdown';
import { DropdownOption } from 'components/Elements/Dropdown/types';
import { CalendarIcon } from 'components/Elements/CustomSVGIcon';
import './incidentFilterModal.scss';

export interface IncidentFilterValues {
    site: string[];
    category: string[];
    remedyReference: string[];
    status: string[];
    priority: string[];
    critical: string[];
    dateLoggedStart: string;
    dateLoggedEnd: string;
}

export interface IncidentFilterOptions {
    sites: DropdownOption[];
    categories: DropdownOption[];
    remedyReferences: DropdownOption[];
    statuses: DropdownOption[];
    priorities: DropdownOption[];
    criticalLevels: DropdownOption[];
}

interface IncidentFilterModalProps {
    visible: boolean;
    onClose: () => void;
    onApply: (values: IncidentFilterValues) => void;
    onClearAll: () => void;
    filterOptions: IncidentFilterOptions;
    initialValues: IncidentFilterValues;
    hideSiteFilter?: boolean;
    // Retail stores have no critical incidents, so their incident list offers
    // no Critical filter (the create form drops the checkbox to match).
    hideCriticalFilter?: boolean;
}

const emptyValues: IncidentFilterValues = {
    site: [],
    category: [],
    remedyReference: [],
    status: [],
    priority: [],
    critical: [],
    dateLoggedStart: '',
    dateLoggedEnd: '',
};

const IncidentFilterModal: React.FC<IncidentFilterModalProps> = ({
    visible,
    onClose,
    onApply,
    onClearAll,
    filterOptions,
    initialValues,
    hideSiteFilter = false,
    hideCriticalFilter = false,
}) => {
    const [localValues, setLocalValues] = useState<IncidentFilterValues>(initialValues);

    useEffect(() => {
        if (visible) {
            setLocalValues(initialValues);
        }
    }, [visible, initialValues]);

    const handleDropdownChange = (field: keyof IncidentFilterValues, values: DropdownOption[]) => {
        setLocalValues(prev => ({
            ...prev,
            [field]: values.map(v => v.value),
        }));
    };

    const handleDateChange = (field: keyof IncidentFilterValues, date: Dayjs | null) => {
        setLocalValues(prev => ({
            ...prev,
            [field]: date ? date.format('YYYY-MM-DD') : '',
        }));
    };

    const handleApply = () => {
        onApply(localValues);
        onClose();
    };

    const handleClearAll = () => {
        setLocalValues(emptyValues);
        onClearAll();
    };

    const getSelectedValues = (field: string[], options: DropdownOption[]): DropdownOption[] => options.filter(opt => field.includes(opt.value));

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            width={1200}
            className="filter-modal"
            centered>
            <div className="filter-modal-container">
                <h2 className="heading">Filters</h2>
                <div className="filter-grid">
                    {/* Row 1 */}
                    {!hideSiteFilter && (
                        <div className="filter-field">
                            <h3>Site</h3>
                            <div className="dropdown-wrapper">
                                <Dropdown
                                    options={filterOptions.sites}
                                    onSelect={(values) => handleDropdownChange('site', values)}
                                    values={getSelectedValues(localValues.site, filterOptions.sites)}
                                    multi
                                    placeholder="Select Site" />
                            </div>
                        </div>
                    )}
                    <div className="filter-field">
                        <h3>Category</h3>
                        <div className="dropdown-wrapper">
                            <Dropdown
                                options={filterOptions.categories}
                                onSelect={(values) => handleDropdownChange('category', values)}
                                values={getSelectedValues(localValues.category, filterOptions.categories)}
                                multi
                                placeholder="Select Category" />
                        </div>
                    </div>
                    <div className="filter-field">
                        <h3>Remedy Reference</h3>
                        <div className="dropdown-wrapper">
                            <Dropdown
                                options={filterOptions.remedyReferences}
                                onSelect={(values) => handleDropdownChange('remedyReference', values)}
                                values={getSelectedValues(localValues.remedyReference, filterOptions.remedyReferences)}
                                multi
                                placeholder="Select Remedy Reference" />
                        </div>
                    </div>

                    {/* Row 2 */}
                    <div className="filter-field">
                        <h3>Status</h3>
                        <div className="dropdown-wrapper">
                            <Dropdown
                                options={filterOptions.statuses}
                                onSelect={(values) => handleDropdownChange('status', values)}
                                values={getSelectedValues(localValues.status, filterOptions.statuses)}
                                multi
                                placeholder="Select Status" />
                        </div>
                    </div>
                    <div className="filter-field">
                        <h3>Priority</h3>
                        <div className="dropdown-wrapper">
                            <Dropdown
                                options={filterOptions.priorities}
                                onSelect={(values) => handleDropdownChange('priority', values)}
                                values={getSelectedValues(localValues.priority, filterOptions.priorities)}
                                multi
                                placeholder="Select Priority" />
                        </div>
                    </div>
                    {!hideCriticalFilter && (
                        <div className="filter-field">
                            <h3>Critical</h3>
                            <div className="dropdown-wrapper">
                                <Dropdown
                                    options={filterOptions.criticalLevels}
                                    onSelect={(values) => handleDropdownChange('critical', values)}
                                    values={getSelectedValues(localValues.critical, filterOptions.criticalLevels)}
                                    multi
                                    placeholder="Select Critical Level" />
                            </div>
                        </div>
                    )}

                    {/* Row 3 */}
                    <div className="filter-field">
                        <h3>Date Logged</h3>
                        <div className="date-range">
                            <DatePicker
                                value={localValues.dateLoggedStart ? dayjs(localValues.dateLoggedStart) : null}
                                onChange={(date) => handleDateChange('dateLoggedStart', date)}
                                placeholder="Start Date"
                                format="DD/MM/YYYY"
                                disabledDate={(current) => (localValues.dateLoggedEnd ? current.isAfter(dayjs(localValues.dateLoggedEnd), 'day') : false)}
                                suffixIcon={<CalendarIcon />}
                                style={{ width: '100%' }} />
                            <DatePicker
                                value={localValues.dateLoggedEnd ? dayjs(localValues.dateLoggedEnd) : null}
                                onChange={(date) => handleDateChange('dateLoggedEnd', date)}
                                placeholder="End Date"
                                format="DD/MM/YYYY"
                                disabledDate={(current) => (localValues.dateLoggedStart ? current.isBefore(dayjs(localValues.dateLoggedStart), 'day') : false)}
                                suffixIcon={<CalendarIcon />}
                                style={{ width: '100%' }} />
                        </div>
                    </div>
                </div>

                <div className="button-wrapper">
                    <button type="button" className="clear-button" onClick={handleClearAll}>Clear all</button>
                    <button type="button" className="apply-button" onClick={handleApply}>Apply</button>
                    <button type="button" className="cancel-button" onClick={onClose}>Close</button>
                </div>
            </div>
        </Modal>
    );
};

export default IncidentFilterModal;
